import React, { useState } from "react";
import { MdTranslate } from "react-icons/md";
import {
  TRANSLATE_ERROR_ANONYMITY,
  TRANSLATE_ERROR_DISABLED,
  TRANSLATE_ERROR_GENERIC,
  TRANSLATE_MODE_ENGLISH,
  TRANSLATE_MODE_ORIGINAL,
  TRANSLATE_NOTICE_BUSY,
  TRANSLATE_NOTICE_RATE_LIMITED,
  TRANSLATE_STATUS_RUNNING,
} from "../../../../utils/translateResponsesCore";
import {
  TRANSLATE_LABEL,
  TRANSLATE_MESSAGE_ANONYMITY,
  TRANSLATE_MESSAGE_BUSY,
  TRANSLATE_MESSAGE_DISABLED,
  TRANSLATE_MESSAGE_ERROR,
  TRANSLATE_MESSAGE_MASKED,
  TRANSLATE_MESSAGE_RATE_LIMITED,
  TRANSLATE_MESSAGE_UNAVAILABLE,
  TRANSLATE_MESSAGE_WINDOW,
  TRANSLATE_OPTION_ENGLISH,
  TRANSLATE_OPTION_ORIGINAL,
  TRANSLATE_RETRY_LABEL,
} from "../../../../utils/triviaResultsStrings";
import "./TranslateToggle.css";

let translateToggleInstances = 0;

const STATUS_TEXT_CLASS = "translate-toggle-status-text";

// Shared Results "Translate" control: an icon label and an Original / English segmented switch.
// `translation` is the useTranslatedResponses return value (or any { mode, setMode } on pages
// with no answers to translate), so By User and By Question render the exact same control.
// English translates the responses on screen (plus one screen below) as the admin scrolls.
// `action` (the Export CSV button) sits right of the switch, status text under it.
// `hideProgress`: no open-text answers to count, so only errors show under the switch.
const TranslateToggle = ({ translation, action, hideProgress }) => {
  const [uid] = useState(() => {
    translateToggleInstances += 1;
    return translateToggleInstances;
  });
  const { mode, setMode, status, progress, error, retry } = translation;
  const labelId = `translate-toggle-label-${uid}`;
  const groupName = `translate-toggle-mode-${uid}`;

  const renderStatus = () => {
    if (error) {
      if (error.kind === TRANSLATE_ERROR_GENERIC) {
        return (
          <p className={`${STATUS_TEXT_CLASS} text-danger`}>
            {TRANSLATE_MESSAGE_ERROR}
            <button type="button" className="translate-toggle-retry" onClick={retry}>
              {TRANSLATE_RETRY_LABEL}
            </button>
          </p>
        );
      }
      let message = TRANSLATE_MESSAGE_UNAVAILABLE;
      if (error.kind === TRANSLATE_ERROR_ANONYMITY) {
        message = TRANSLATE_MESSAGE_ANONYMITY;
      } else if (error.kind === TRANSLATE_ERROR_DISABLED) {
        message = TRANSLATE_MESSAGE_DISABLED;
      }
      return <p className={STATUS_TEXT_CLASS}>{message}</p>;
    }
    if (hideProgress || mode !== TRANSLATE_MODE_ENGLISH) {
      return null;
    }
    const isRunning = status === TRANSLATE_STATUS_RUNNING;
    if (isRunning && progress && progress.notice === TRANSLATE_NOTICE_BUSY) {
      return <p className={STATUS_TEXT_CLASS}>{TRANSLATE_MESSAGE_BUSY}</p>;
    }
    if (isRunning && progress && progress.notice === TRANSLATE_NOTICE_RATE_LIMITED) {
      return <p className={STATUS_TEXT_CLASS}>{TRANSLATE_MESSAGE_RATE_LIMITED}</p>;
    }
    if (translation.masked) {
      return <p className={STATUS_TEXT_CLASS}>{TRANSLATE_MESSAGE_MASKED}</p>;
    }
    if (isRunning) {
      return <p className={STATUS_TEXT_CLASS}>{translation.runningMessage || TRANSLATE_MESSAGE_WINDOW}</p>;
    }
    return null;
  };

  const renderOption = (value, label) => {
    const checked = mode === value;
    return (
      <label className={`translate-toggle-option${checked ? " is-checked" : ""}`}>
        <input
          type="radio"
          name={groupName}
          value={value}
          checked={checked}
          onChange={() => setMode(value)}
        />
        {label}
      </label>
    );
  };

  return (
    <div className={`translate-toggle${action ? " has-action" : ""}`}>
      <div className="translate-toggle-main">
        <span id={labelId} className="translate-toggle-label">
          <MdTranslate className="translate-toggle-icon" aria-hidden="true" />
          {TRANSLATE_LABEL}
        </span>
        <div
          role="radiogroup"
          aria-labelledby={labelId}
          className="translate-toggle-options"
        >
          {renderOption(TRANSLATE_MODE_ORIGINAL, TRANSLATE_OPTION_ORIGINAL)}
          {renderOption(TRANSLATE_MODE_ENGLISH, TRANSLATE_OPTION_ENGLISH)}
        </div>
        {action && <span className="translate-toggle-divider" aria-hidden="true" />}
        {action && <div className="translate-toggle-action">{action}</div>}
      </div>
      <div className="translate-toggle-status" aria-live="polite">
        {renderStatus()}
      </div>
    </div>
  );
};

export default TranslateToggle;
